import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  Field,
  Input,
  NativeSelect,
  RadioCard,
  RadioGroup,
} from '@provenance/design';
import {
  AU_STATES,
  AU_STATE_LABELS,
  formatAbn,
  isValidAbn,
  publicRegistrationSchema,
  type ChamberBranding,
  type PublicRegistrationInput,
} from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, CheckCircle2, Info, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PublicApiError, publicRequest } from '../api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * PUBLIC REGISTRATION — spec §10
 *
 * One form, many chambers: the applicant picks their Certificate of Origin provider and
 * the form adopts that chamber's branding. The platform stays in the background.
 *
 * Decisions this page embodies:
 *
 *  EMAIL IS THE USERNAME. The legacy system asked for a separate chosen username, which
 *  is one more thing to forget and one more collision to resolve, for no benefit (§10.1).
 *
 *  NO PASSWORD FIELD. The applicant is not creating an account — they are making a
 *  request a chamber must approve. The password is set later, from the activation link,
 *  which means no password exists in a mailbox or in this form's POST body.
 *
 *  THE ABN IS CHECKED HERE, BEFORE SUBMITTING. The ABR checksum runs in the browser,
 *  offline, and catches a transposed digit immediately rather than after a chamber
 *  operator has spent a day on it.
 *
 *  IT SAYS WHAT HAPPENS NEXT. An applicant who submits and hears nothing assumes it
 *  failed. The confirmation is explicit about who reviews it and what arrives by email.
 * ════════════════════════════════════════════════════════════════════════════
 */
export function Register() {
  const [submitted, setSubmitted] = useState<{ companyName: string; chamber: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const chambers = useQuery({
    queryKey: ['public-chambers'],
    queryFn: () => publicRequest<ChamberBranding[]>('/api/public/chambers'),
  });

  const form = useForm<PublicRegistrationInput>({
    resolver: zodResolver(publicRegistrationSchema),
    defaultValues: {
      chamberId: '',
      companyName: '',
      abn: '',
      applicantType: 'EXPORTER',
      tradingName: '',
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhone: '',
      address: { line1: '', line2: '', suburb: '', state: 'QLD', postcode: '', country: 'AU' },
      signatories: [{ fullName: '', position: '', email: '', specimenFilename: '', specimenBase64: '' }],
      acceptedTerms: undefined as never,
    },
  });

  const signatories = form.watch('signatories') ?? [];

  /**
   * Reads a chosen specimen into the form.
   *
   * Chunked base64 rather than a single spread: `String.fromCharCode(...bytes)` on a
   * multi-megabyte scan blows the argument limit and throws, which reads to an applicant
   * as "the upload failed" for a file that is perfectly fine.
   */
  const readSpecimen = async (index: number, file: File) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    form.setValue(`signatories.${index}.specimenFilename`, file.name, { shouldValidate: true });
    form.setValue(`signatories.${index}.specimenBase64`, btoa(binary), { shouldValidate: true });
  };

  const selectedSlug = form.watch('chamberId');
  const selectedChamber = (chambers.data ?? []).find((c) => c.slug === selectedSlug);

  // The chamber's brand is adopted the moment one is chosen (spec §5) — the applicant is
  // registering with a chamber, not with us.
  useEffect(() => {
    const root = document.documentElement;
    if (selectedChamber) {
      root.style.setProperty('--pv-brand', selectedChamber.primaryColor);
      root.style.setProperty('--pv-accent', selectedChamber.secondaryColor);
    } else {
      root.style.removeProperty('--pv-brand');
      root.style.removeProperty('--pv-accent');
    }
  }, [selectedChamber]);

  const abnValue = form.watch('abn');
  const abnLooksValid = abnValue?.replace(/\s/g, '').length === 11 && isValidAbn(abnValue);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await publicRequest('/api/v1/public/register', { method: 'POST', body: values });
      setSubmitted({
        companyName: values.companyName,
        chamber: selectedChamber?.legalName ?? 'the chamber',
      });
    } catch (error) {
      if (error instanceof PublicApiError) {
        // Field-level errors from the server are attached to the right inputs rather
        // than dumped in a banner the applicant has to map back themselves.
        const fields = error.fieldErrors;
        let attached = false;
        for (const [path, message] of Object.entries(fields)) {
          form.setError(path as keyof PublicRegistrationInput, { message });
          attached = true;
        }
        if (!attached) setFormError(error.message);
      } else {
        setFormError('Could not submit the form. Check your connection and try again.');
      }
    }
  });

  if (submitted) {
    return (
      <div className="mx-auto w-full max-w-[40rem] px-4 py-12 sm:py-20">
        <Card className="!border-[var(--pv-ok-line)] !bg-[var(--pv-ok-bg)]">
          <div className="flex items-start gap-3">
            <CheckCircle2 aria-hidden className="mt-0.5 size-7 shrink-0 text-[var(--pv-ok)]" />
            <div className="min-w-0">
              <h1 className="text-[1.4rem] font-bold leading-tight tracking-[-0.02em] text-[var(--pv-ok)]">
                Your application is with {submitted.chamber}
              </h1>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--pv-ink-soft)]">
                {submitted.companyName} has been submitted for review. An operator at the chamber
                will look at it, usually within a business day or two.
              </p>
              <h2 className="mt-5 text-[13px] font-semibold text-[var(--pv-ok)]">
                What happens next
              </h2>
              <ol className="mt-2 flex list-none flex-col gap-2.5 p-0">
                {[
                  'You will get an email confirming we have it.',
                  'If the chamber needs anything further — a certificate of incorporation, proof of address — they will email you a link to provide it. You will not need an account to use that link.',
                  'Once approved, you will get a link to set your password and sign in.',
                ].map((step, index) => (
                  <li key={step} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2.5">
                    <span className="grid size-5 shrink-0 place-items-center rounded-full bg-[var(--pv-ok)] text-[11px] font-bold text-white">
                      {index + 1}
                    </span>
                    <span className="text-[13.5px] leading-relaxed text-[var(--pv-ink-soft)]">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[52rem] px-4 py-10 sm:py-16">
      <header>
        <div className="flex items-center gap-2.5">
          {selectedChamber?.logoUrl ? (
            <img src={selectedChamber.logoUrl} alt={selectedChamber.legalName} className="h-9 max-w-[220px] object-contain" />
          ) : (
            <>
              <span className="grid size-9 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-ink)] [font-size:15px] font-bold text-[var(--pv-leaf)]">
                P
              </span>
              <span className="[font-size:16px] font-bold tracking-[-0.02em]">Provenir</span>
            </>
          )}
        </div>

        <p className="pv-eyebrow mt-8">Registration</p>
        <h1 className="pv-display mt-2">
          Register to obtain certificates of origin.
        </h1>
        <p className="pv-lede mt-4">
          Australian exporters and freight forwarders register with a Chamber of Commerce to have
          certificates of origin issued. Choose your chamber below — the rest of the form is the
          same whichever you pick.
        </p>
      </header>

      <form onSubmit={onSubmit} noValidate className="mt-8 flex flex-col gap-4">
        {formError && (
          <Callout tone="stop" icon={<AlertTriangle className="size-4" />}>
            {formError}
          </Callout>
        )}

        <Card>
          <CardHeader
            title="Your chamber"
            description="The Chamber of Commerce that will review your application and issue your certificates."
          />
          {chambers.isLoading ? (
            <p className="text-[13px] text-[var(--pv-muted)]">Loading chambers…</p>
          ) : (
            <Field label="Certificate of origin provider" required error={form.formState.errors.chamberId?.message}>
              <NativeSelect {...form.register('chamberId')}>
                <option value="">Choose your chamber…</option>
                {(chambers.data ?? []).map((chamber) => (
                  <option key={chamber.slug} value={chamber.slug}>
                    {chamber.legalName}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          )}
          {selectedChamber && (
            <Callout tone="ok" className="mt-3.5" icon={<ShieldCheck className="size-4" />}>
              Your application goes to <strong>{selectedChamber.legalName}</strong>. Certificates
              they issue are verifiable by anyone who receives them.
            </Callout>
          )}
        </Card>

        <Card>
          <CardHeader title="Your company" description="As registered with the Australian Business Register." />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Company name"
              required
              className="sm:col-span-2"
              error={form.formState.errors.companyName?.message}
            >
              <Input placeholder="Agricomm Trading Australia Pty Ltd" {...form.register('companyName')} />
            </Field>

            <Field
              label="ABN"
              required
              error={form.formState.errors.abn?.message}
              help="Eleven digits. Checked against the ABR checksum as you type."
              hint={
                abnLooksValid ? (
                  <Badge tone="ok" size="sm">
                    {formatAbn(abnValue)}
                  </Badge>
                ) : undefined
              }
            >
              <Input mono inputMode="numeric" placeholder="53 004 085 616" {...form.register('abn')} />
            </Field>

            <Field label="Trading name" help="If it differs from the registered company name.">
              <Input {...form.register('tradingName')} />
            </Field>

            <fieldset className="m-0 border-0 p-0 sm:col-span-2">
              <legend className="mb-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
                What kind of business are you? <span className="font-bold text-[var(--pv-stop)]">*</span>
              </legend>
              <RadioGroup
                value={form.watch('applicantType')}
                onValueChange={(v) => form.setValue('applicantType', v as 'EXPORTER' | 'FREIGHT_FORWARDER')}
                className="grid gap-2 sm:grid-cols-2"
              >
                <RadioCard
                  value="EXPORTER"
                  title="Exporter"
                  description="You export goods that you produce or buy."
                />
                <RadioCard
                  value="FREIGHT_FORWARDER"
                  title="Freight forwarder"
                  description="You arrange documentation and shipping on an exporter's behalf."
                />
              </RadioGroup>
            </fieldset>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Primary contact"
            description="The person the chamber will deal with. This email address becomes the sign-in for your account."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" required error={form.formState.errors.contactFirstName?.message}>
              <Input autoComplete="given-name" {...form.register('contactFirstName')} />
            </Field>
            <Field label="Last name" required error={form.formState.errors.contactLastName?.message}>
              <Input autoComplete="family-name" {...form.register('contactLastName')} />
            </Field>
            <Field
              label="Business email"
              required
              error={form.formState.errors.contactEmail?.message}
              help="You will sign in with this. There is no separate username."
            >
              <Input type="email" autoComplete="email" {...form.register('contactEmail')} />
            </Field>
            <Field label="Business phone" required error={form.formState.errors.contactPhone?.message}>
              <Input type="tel" autoComplete="tel" placeholder="(07) 3369 4400" {...form.register('contactPhone')} />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader title="Business address" description="Your registered Australian business address." />
          <div className="grid gap-4 sm:grid-cols-6">
            <Field
              label="Street address"
              required
              className="sm:col-span-6"
              error={form.formState.errors.address?.line1?.message}
            >
              <Input autoComplete="address-line1" {...form.register('address.line1')} />
            </Field>
            <Field label="Address line 2" className="sm:col-span-6">
              <Input autoComplete="address-line2" {...form.register('address.line2')} />
            </Field>
            <Field
              label="Suburb"
              required
              className="sm:col-span-3"
              error={form.formState.errors.address?.suburb?.message}
            >
              <Input autoComplete="address-level2" {...form.register('address.suburb')} />
            </Field>
            <Field label="State" required className="sm:col-span-2">
              <NativeSelect {...form.register('address.state')}>
                {AU_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state} — {AU_STATE_LABELS[state]}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Postcode"
              required
              className="sm:col-span-1"
              error={form.formState.errors.address?.postcode?.message}
            >
              <Input mono inputMode="numeric" maxLength={4} {...form.register('address.postcode')} />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Who may sign your declarations"
            description="The people authorised to sign export declarations for your company, and a scan of each signature. Your chamber checks the signature on every declaration against the specimen you provide here, so at least one is required."
          />

          <div className="flex flex-col gap-4">
            {signatories.map((sig, index) => (
              <div
                key={index}
                className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3.5"
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="Full name"
                    required
                    error={form.formState.errors.signatories?.[index]?.fullName?.message}
                  >
                    <Input {...form.register(`signatories.${index}.fullName`)} />
                  </Field>
                  <Field
                    label="Position"
                    required
                    help="Their role in your export operation."
                    error={form.formState.errors.signatories?.[index]?.position?.message}
                  >
                    <Input
                      placeholder="Export Manager"
                      {...form.register(`signatories.${index}.position`)}
                    />
                  </Field>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Email">
                    <Input type="email" {...form.register(`signatories.${index}.email`)} />
                  </Field>
                  <Field
                    label="Specimen signature"
                    required
                    help="A scan or photo of their handwritten signature. PNG or JPEG."
                    error={form.formState.errors.signatories?.[index]?.specimenBase64?.message}
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void readSpecimen(index, file);
                      }}
                      aria-label={`Specimen signature for signatory ${index + 1}`}
                      className="[font-size:13px] file:mr-3 file:rounded-[var(--pv-radius-sm)] file:border file:border-[var(--pv-line)] file:bg-[var(--pv-paper)] file:px-3 file:py-1.5 file:[font-size:13px]"
                    />
                    {sig.specimenFilename && (
                      <p className="mt-1 [font-size:12px] text-[var(--pv-ok)]">
                        {sig.specimenFilename} attached
                      </p>
                    )}
                  </Field>
                </div>

                {signatories.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      form.setValue(
                        'signatories',
                        signatories.filter((_, i) => i !== index),
                        { shouldValidate: true },
                      )
                    }
                    className="mt-2 [font-size:12.5px] font-medium text-[var(--pv-stop)] hover:underline"
                  >
                    Remove this person
                  </button>
                )}
              </div>
            ))}
          </div>

          {signatories.length < 10 && (
            <button
              type="button"
              onClick={() =>
                form.setValue(
                  'signatories',
                  [
                    ...signatories,
                    { fullName: '', position: '', email: '', specimenFilename: '', specimenBase64: '' },
                  ],
                  { shouldValidate: false },
                )
              }
              className="mt-3 [font-size:13px] font-medium text-[var(--pv-brand)] hover:underline"
            >
              + Add another person
            </button>
          )}
        </Card>

        <Card>
          <Checkbox
            checked={form.watch('acceptedTerms') === true}
            onCheckedChange={(checked) =>
              form.setValue('acceptedTerms', (checked === true) as true, { shouldValidate: true })
            }
            label="I am authorised to register this business, and the information above is true and complete."
            description="Certificates of origin are official trade documents. A chamber relies on this declaration when it issues one in your name."
          />
          {form.formState.errors.acceptedTerms && (
            <p role="alert" className="mt-2 [font-size:var(--pv-text-xs)] text-[var(--pv-stop)]">
              {form.formState.errors.acceptedTerms.message}
            </p>
          )}
        </Card>

        <Callout tone="info" icon={<Info className="size-4" />}>
          No password is set here. Once the chamber approves your registration, you will get a link
          to choose one — so your password never travels through this form or sits in an inbox.
        </Callout>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={form.formState.isSubmitting}
          trailingIcon={<ArrowRight className="size-4" />}
          className="w-full"
        >
          Submit for review
        </Button>

        <p className="text-center [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          Already registered?{' '}
          <a href="/verify" className="underline">
            Verify a certificate
          </a>{' '}
          instead.
        </p>
      </form>
    </div>
  );
}
